import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { noticeApi, BannerNotice } from '@/api/noticeApi';

const BANNER_CLOSE_KEY = 'closedBannerIds';
const SLIDE_INTERVAL = 5000;

function getClosedIds(): number[] {
  try {
    const raw = sessionStorage.getItem(BANNER_CLOSE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function addClosedId(id: number) {
  const ids = getClosedIds();
  if (!ids.includes(id)) {
    sessionStorage.setItem(BANNER_CLOSE_KEY, JSON.stringify([...ids, id]));
  }
}

export default function NoticeBanner() {
  const navigate = useNavigate();
  const [banners, setBanners] = useState<BannerNotice[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    noticeApi.getBanner()
      .then(res => {
        const closedIds = getClosedIds();
        const visible = (res.data || []).filter((b: BannerNotice) => !closedIds.includes(b.id));
        setBanners(visible);
      })
      .catch(() => {/* 배너 오류는 무시 */});
  }, []);

  // 자동 슬라이드 (배너 2개 이상)
  useEffect(() => {
    if (banners.length < 2 || dismissed) return;
    const timer = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % banners.length);
    }, SLIDE_INTERVAL);
    return () => clearInterval(timer);
  }, [banners.length, dismissed]);

  const closeCurrent = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    const closed = banners[currentIndex];
    if (!closed) return;
    addClosedId(closed.id);
    const next = banners.filter(b => b.id !== closed.id);
    setBanners(next);
    setCurrentIndex(prev => Math.max(0, Math.min(prev, next.length - 1)));
    if (next.length === 0) setDismissed(true);
  }, [banners, currentIndex]);

  const prev = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex(i => (i - 1 + banners.length) % banners.length);
  };

  const next = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex(i => (i + 1) % banners.length);
  };

  if (dismissed || banners.length === 0) return null;

  const banner = banners[currentIndex];

  return (
    <div
      className="w-full bg-blue-600 text-white cursor-pointer select-none"
      onClick={() => navigate(`/notices/${banner.id}`)}
      role="banner"
      aria-label="공지사항 배너"
    >
      <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center gap-3">
        {/* 좌측 슬라이드 (2개 이상) */}
        {banners.length > 1 && (
          <button
            onClick={prev}
            className="flex-shrink-0 p-1 rounded hover:bg-blue-500 transition-colors"
            aria-label="이전 공지"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
        )}

        {/* 공지 텍스트 */}
        <p className="flex-1 text-sm font-medium text-center truncate">
          <span className="bg-white/20 text-white text-xs font-semibold px-2 py-0.5 rounded mr-2">공지</span>
          {banner.title}
        </p>

        {/* 우측 슬라이드 + 닫기 */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {banners.length > 1 && (
            <>
              <button
                onClick={next}
                className="p-1 rounded hover:bg-blue-500 transition-colors"
                aria-label="다음 공지"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              <span className="text-xs text-blue-200 mx-1">
                {currentIndex + 1}/{banners.length}
              </span>
            </>
          )}
          <button
            onClick={closeCurrent}
            className="p-1 rounded hover:bg-blue-500 transition-colors"
            aria-label="닫기"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
